import React, { useEffect, useRef } from "react";
import { CheckCircle, CircleOutlined, Error } from "@mui/icons-material";
import { Chip, CircularProgress, Tooltip, type ChipProps } from "@mui/material";

import { Status, STATUS_CONFIG } from "@utilifeed/core/models/status";

const ICONS = {
  CircleOutlined: <CircleOutlined />,
  CircularProgress: <CircularProgress size={16} />,
  CheckCircle: <CheckCircle />,
  Error: <Error />,
} as const;

type StatusProps = Omit<ChipProps, "color" | "icon" | "label"> & {
  status: Status;
  error?: string;
  label?: string;
  animated?: boolean;
};

export const StatusComponent: React.FC<StatusProps> = React.memo(
  ({ status, error, label, animated = false, size = "small", ...props }) => {
    const { "data-testid": testId, ...chipProps } = props;
    const timerRef = useRef<NodeJS.Timeout>();

    useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

    const config = STATUS_CONFIG[status];
    const icon = ICONS[config.icon as keyof typeof ICONS];
    const displayLabel = label || config.label;

    const chip = (
      <Chip
        label={displayLabel}
        color={config.color as ChipProps["color"]}
        icon={icon}
        size={size}
        sx={
          animated
            ? {
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 0.8 },
                  "50%": { opacity: 1 },
                },
                animation: "pulse 2s infinite",
              }
            : undefined
        }
        {...chipProps}
      />
    );

    return error ? (
      <Tooltip title={error} arrow data-testid={testId}>
        {chip}
      </Tooltip>
    ) : (
      <div data-testid={testId}>{chip}</div>
    );
  }
);

StatusComponent.displayName = "Status";
